import type { EventClickArg, FormatterInput } from '@fullcalendar/core/index.js'
import FullCalendar from '@fullcalendar/react' // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid' // a plugin!
import interactionPlugin from '@fullcalendar/interaction'
import type { DateClickArg } from '@fullcalendar/interaction' // needed for dayClick
import listPlugin from '@fullcalendar/list'
import type { HTMLAttributes } from 'react'
import { logger } from '@tunasong/models'
import type { WithID } from '@tunasong/models'
import type { CalendarEvent } from '@tunasong/schemas'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { toFullCalendarEvent } from '../lib/to-full-calendar.js'
import './calendar.css'
import cn from 'classnames'
import moduleStyles from './calendar.module.css'

const views = {
  dayGridMonth: {},
  listWeek: {},
  weekGrid: {},
  dayGridDay: {},
}

export interface CalendarProps extends HTMLAttributes<HTMLDivElement> {
  events: WithID<CalendarEvent>[]
  locale?: string
  initialView?: 'listWeek' | 'dayGrid' | 'dayGridMonth' | 'listMonth'
  toolbar?: boolean
  onDateClick?: (date: Date) => void
  onSelectEvent?: (event: CalendarEvent) => void
}

export const Calendar = ({
  events,
  onSelectEvent,
  locale = 'en',
  toolbar = true,
  initialView,
  onDateClick,
  ...divProps
}: CalendarProps) => {
  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      if (!onSelectEvent) {
        return
      }
      const event = events.find(e => e.id === info.event.id)
      invariant(event, 'Event not found')
      onSelectEvent(event)
    },
    [events, onSelectEvent]
  )
  const handleDateClick = useCallback(
    (info: DateClickArg) => {
      logger.debug(info)
      if (onDateClick) {
        onDateClick(info.date)
      }
    },
    [onDateClick]
  )

  const fullCalendarEvents = events.map(toFullCalendarEvent)

  const eventTimeFormat: FormatterInput =
    navigator.language === 'en_US'
      ? {
          hour12: true,
          hour: '2-digit',
          minute: '2-digit',
        }
      : {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        }

  return (
    <div {...divProps} className={cn(moduleStyles.container, divProps?.className)} style={divProps?.style}>
      <FullCalendar
        locale={locale}
        eventTimeFormat={eventTimeFormat}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        events={fullCalendarEvents}
        plugins={[interactionPlugin, dayGridPlugin, listPlugin]}
        views={views}
        weekNumberCalculation="ISO"
        initialView={initialView}
        eventColor={'var(--tuna-palette-secondary-main)'}
        eventTextColor={'var(--tuna-palette-secondary-contrastText)'}
        headerToolbar={
          toolbar
            ? {
                left: 'title', // 'prev,next',
                // center: 'title',
                // right: 'dayGridMonth,dayGridWeek,dayGridDay', // user can switch between the two
              }
            : false
        }
      />
    </div>
  )
}
